import { ActivityType } from "@/types/activity"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { formatDateString, formatDurationFromStrings, formatDate, formatDateForActivityTable } from "../../../lib/utils/date"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Edit, Trash2, Droplet, Milk, Baby } from "lucide-react"
import { cn } from "@/lib/utils"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useToast } from "@/components/ui/use-toast"

interface FeedingTableProps {
  activities: ActivityType[]
  onActivityClick?: (activity: ActivityType) => void
  onEditActivity?: (activity: ActivityType) => void
  onDeleteActivity?: (activity: ActivityType) => void
  timezone?: string
}

export function FeedingTable({ 
  activities, 
  onActivityClick = () => {}, 
  onEditActivity = () => {},
  onDeleteActivity = () => {},
  timezone
}: FeedingTableProps) {
  if (!activities || activities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 px-4 text-center bg-white/5 rounded-lg border border-white/10 min-h-[300px]">
        <div className="mb-4 bg-pink-900/30 text-pink-200 p-5 rounded-full">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-14 w-14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M8 8a3 3 0 0 1 3-3h2a3 3 0 0 1 3 3v1h1a2 2 0 0 1 2 2v4a7 7 0 0 1-7 7h0a7 7 0 0 1-7-7v-4a2 2 0 0 1 2-2h1Z" />
            <path d="M19 9h-1.6a1 1 0 0 0-1 1.05" />
            <path d="M12 19v1" />
            <path d="M7.6 10.05a1 1 0 0 0-1-1.05H5.07" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-pink-200 mb-2">No Feeding Records Yet</h3>
        <p className="text-muted-foreground text-sm max-w-xs">
          Keep track of your baby's feeding schedule to ensure they're receiving proper nutrition.
        </p>
      </div>
    );
  }

  const handleRowClick = (activity: ActivityType) => {
    onActivityClick(activity);
  };

  const { toast } = useToast();

  // Helper function to render feeding type icon
  const renderFeedingTypeIcon = (type: string) => {
    switch (type) {
      case 'breastfeeding':
        return <Baby className="h-4 w-4 mr-2 text-pink-400" />;
      case 'bottle':
        return <Milk className="h-4 w-4 mr-2 text-blue-400" />;
      default:
        return null;
    }
  };

  // Helper to format feeding details
  const renderFeedingDetails = (activity: ActivityType) => {
    if (activity.feeding_type === 'breastfeeding') {
      // For breastfeeding, show sides used
      const sides = [];
      if (activity.left_duration && activity.left_duration > 0) sides.push('Left');
      if (activity.right_duration && activity.right_duration > 0) sides.push('Right');
      
      return (
        <div className="space-y-1">
          <div className="flex items-center">
            <Badge variant={sides.length > 1 ? "outline" : "secondary"} className="font-normal">
              {sides.join(' & ')}
            </Badge>
          </div>
          {activity.left_duration && activity.left_duration > 0 && (
            <div className="text-xs text-gray-400">Left: {activity.left_duration} min</div>
          )}
          {activity.right_duration && activity.right_duration > 0 && (
            <div className="text-xs text-gray-400">Right: {activity.right_duration} min</div>
          )}
        </div>
      );
    } else if (activity.feeding_type === 'bottle') {
      // For bottle, show amount and milk type/brand if available
      const milkType = (activity as any).milk_type || (activity as any).milkType;
      const brand = activity.brand;
      
      return activity.feeding_amount ? (
        <div className="space-y-1">
          <Badge variant="outline" className={cn(
            "font-normal",
            milkType === 'Formula' 
              ? "bg-yellow-500/20 border-yellow-500/40 text-yellow-200"
              : "bg-blue-500/20 border-blue-500/40 text-blue-200"
          )}>
            {activity.feeding_amount} {activity.feeding_unit || 'oz'}
            {milkType && ` - ${milkType === 'Breastmilk' ? 'Breast Milk' : 'Formula'}`}
          </Badge>
          {activity.feeding_amount && (
            <div className="text-xs text-gray-300">{Math.round(Number(activity.feeding_amount) * 29.5735)} ml</div>
          )}
          {brand && milkType === 'Formula' && (
            <div className="text-xs text-gray-300">Brand: {brand}</div>
          )}
        </div>
      ) : 'N/A';
    }
    
    return 'N/A';
  };

  return (
    <div className="rounded-md border border-border">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-800">
            <TableHead className="text-slate-300 font-medium">Start Time</TableHead>
            <TableHead className="text-slate-300 font-medium">End Time</TableHead>
            <TableHead className="text-slate-300 font-medium">Duration</TableHead>
            <TableHead className="text-slate-300 font-medium">Type</TableHead>
            <TableHead className="text-slate-300 font-medium">Details</TableHead>
            <TableHead className="text-slate-300 font-medium">Notes</TableHead>
            <TableHead className="text-right text-slate-300 font-medium">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {activities.map((activity) => (
            <TableRow 
              key={activity.id} 
              className="cursor-pointer hover:bg-white/5"
              onClick={() => handleRowClick(activity)}
            >
              <TableCell className="font-medium text-slate-300">
                {formatDateForActivityTable(activity.startTime)}
              </TableCell>
              <TableCell className="text-slate-300">
                {formatDateForActivityTable(activity.endTime)}
              </TableCell>
              <TableCell onClick={() => handleRowClick(activity)} className="cursor-pointer text-slate-300">
                {formatDurationFromStrings(activity.startTime, activity.endTime)}
              </TableCell>
              <TableCell onClick={() => handleRowClick(activity)} className="cursor-pointer">
                <div className="flex items-center">
                  {renderFeedingTypeIcon(activity.feeding_type || '')}
                  <Badge variant="outline" className={cn(
                    "capitalize font-medium",
                    activity.feeding_type === 'breastfeeding' && "bg-pink-500/30 border-pink-500/40 text-pink-100",
                    activity.feeding_type === 'bottle' && "bg-blue-500/30 border-blue-500/40 text-blue-100",
                    activity.feeding_type === 'formula' && "bg-yellow-500/30 border-yellow-500/40 text-yellow-100"
                  )}>
                    {activity.feeding_type || 'Unknown'}
                  </Badge>
                </div>
              </TableCell>
              <TableCell onClick={() => handleRowClick(activity)} className="cursor-pointer text-slate-300">
                {renderFeedingDetails(activity)}
              </TableCell>
              <TableCell onClick={() => handleRowClick(activity)} className="cursor-pointer text-slate-300">
                {activity.notes ? (
                  activity.notes.length > 20 ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger className="text-left cursor-help">
                          {activity.notes.substring(0, 20)}...
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-xs">
                          <p className="text-sm">{activity.notes}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    activity.notes
                  )
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={(e) => {
                      e.stopPropagation();
                      onActivityClick(activity);
                    }}
                    className="h-8 w-8 text-blue-400/80 hover:text-blue-300 hover:bg-blue-500/20"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteActivity(activity);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
